import {
  createFinanceSalaryCompanyApi,
  fetchFinanceSalaryCompanies,
  fetchFinanceSalaryCompanyById,
  updateFinanceSalaryCompanyApi,
} from "../company/financeSalaryCompaniesApi";
import {
  DEFAULT_SALARY_COMPANY_NAME,
  DEFAULT_SALARY_COMPANY_TAB_ID,
  findTheCodingStudioCompany,
  isTheCodingStudioCompanyName,
  isTheCodingStudioTabActive,
} from "../company/financeDefaultSalaryCompany";

const PAYSLIP_COMPANY_PLACEHOLDER = {
  contactNumber: "0000000000",
  emailId: "payslip@company.local",
  companyAddress: "—",
};

export const emptyPayslipCompanyForm = () => ({
  id: "",
  companyName: "",
  ownerName: "",
  contactNumber: "",
  emailId: "",
  companyAddress: "",
  signatureFileName: "",
  signatureDataUrl: "",
});

const normalizeCompanyName = (value) => String(value || "").trim().toLowerCase();

/** Match finance company by exact company name (case-insensitive). */
export const findFinanceCompanyByName = (companyName, companies = []) => {
  const key = normalizeCompanyName(companyName);
  if (!key) return null;
  return companies.find((row) => normalizeCompanyName(row?.companyName) === key) || null;
};

/** Resolve finance company from tab id, record id, or default (The Coding Studio). */
export const resolveFinanceCompanyForPayslip = (companyKey, companies = []) => {
  const key = String(companyKey ?? "").trim();
  if (key && key !== DEFAULT_SALARY_COMPANY_TAB_ID) {
    const byId = companies.find((row) => String(row.id) === key);
    if (byId) return byId;
  }
  return findTheCodingStudioCompany(companies);
};

const profileToPayslipForm = (profile) => {
  if (!profile) return emptyPayslipCompanyForm();
  const signatureUrl = profile.signatureUrl || profile.signatureDataUrl || "";
  return {
    id: profile.id != null ? String(profile.id) : "",
    companyName: profile.companyName || "",
    ownerName: profile.ownerName || "",
    contactNumber: profile.contactNumber || "",
    emailId: profile.emailId || "",
    companyAddress: profile.companyAddress || "",
    signatureFileName: profile.signatureFileName || "",
    signatureDataUrl: signatureUrl,
  };
};

const defaultFormForTab = (companyKey, companyLabel, companies) => {
  const profile = resolveFinanceCompanyForPayslip(companyKey, companies);
  if (profile) return profileToPayslipForm(profile);

  const label = String(companyLabel || "").trim();
  const isDefaultTab =
    isTheCodingStudioTabActive(companyKey, companies) ||
    !String(companyKey || "").trim() ||
    String(companyKey) === DEFAULT_SALARY_COMPANY_TAB_ID ||
    isTheCodingStudioCompanyName(label);

  return {
    ...emptyPayslipCompanyForm(),
    companyName: isDefaultTab ? DEFAULT_SALARY_COMPANY_NAME : label,
  };
};

export const readPayslipCompanyById = async (companyId) => {
  const profile = await fetchFinanceSalaryCompanyById(companyId);
  return profileToPayslipForm(profile);
};

export const readPayslipCompanyForTab = async (companyKey, companyLabel = "") => {
  const list = await fetchFinanceSalaryCompanies();
  return defaultFormForTab(companyKey, companyLabel, list);
};

export const readPayslipCompany = async () => readPayslipCompanyForTab("");

export const readPayslipCompanyByIdSync = (companyId, companies = []) => {
  return profileToPayslipForm(resolveFinanceCompanyForPayslip(companyId, companies));
};

export const readPayslipCompanyByNameSync = (companyName, companies = []) => {
  const profile = findFinanceCompanyByName(companyName, companies);
  if (profile) return profileToPayslipForm(profile);
  if (isTheCodingStudioCompanyName(companyName)) {
    return profileToPayslipForm(findTheCodingStudioCompany(companies));
  }
  return emptyPayslipCompanyForm();
};

export const savePayslipCompany = async (form, { companyKey } = {}) => {
  const companyName = String(form.companyName || "").trim();
  const ownerName = String(form.ownerName || "").trim();
  if (!companyName) throw new Error("Company name is required.");
  if (!ownerName) throw new Error("Owner / signatory name is required.");

  const list = await fetchFinanceSalaryCompanies();
  const existing =
    resolveFinanceCompanyForPayslip(companyKey ?? form.id, list) ||
    findFinanceCompanyByName(form.companyName, list);

  const payload = {
    companyName,
    ownerName,
    contactNumber:
      String(existing?.contactNumber || "").trim() || PAYSLIP_COMPANY_PLACEHOLDER.contactNumber,
    emailId: String(existing?.emailId || "").trim() || PAYSLIP_COMPANY_PLACEHOLDER.emailId,
    companyAddress:
      String(existing?.companyAddress || "").trim() || PAYSLIP_COMPANY_PLACEHOLDER.companyAddress,
    signatureDataUrl: form.signatureDataUrl || existing?.signatureDataUrl || "",
    signatureFileName: form.signatureFileName || existing?.signatureFileName || "",
  };

  if (existing?.id) {
    const updated = await updateFinanceSalaryCompanyApi(existing.id, {
      ...existing,
      ...payload,
    });
    return profileToPayslipForm(updated);
  }

  const created = await createFinanceSalaryCompanyApi(payload);
  return profileToPayslipForm(created);
};
